import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { ContentPage as Page } from '@shop/shared';
import { api, ApiError } from '../lib/api';
import { useLocale, useT } from '../lib/i18n';
import { Markdown } from '../lib/markdown';
import { useSeo } from '../lib/seo';
import { Breadcrumbs, ErrorState, Ornament, Skeleton } from '../components/ui';
import { NotFoundPage } from './NotFound';

export function ContentPage() {
  const { slug = '' } = useParams();
  const locale = useLocale();
  const t = useT();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['page', locale, slug],
    queryFn: () => api<Page>(`/api/store/pages/${encodeURIComponent(slug)}`),
    retry: (c, e) => !(e instanceof ApiError && e.status === 404) && c < 2,
  });
  useSeo({ title: data?.seoTitle || data?.title, description: data?.seoDescription || data?.body.replace(/[#*\-\n]+/g, ' ').slice(0, 200) });
  if (error instanceof ApiError && error.status === 404) return <NotFoundPage />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  return (
    <article className="container-page pt-8 pb-24 md:pt-12">
      <Breadcrumbs items={[{ label: t.common.home, to: '/' }, { label: data?.title ?? '…' }]} />
      <div className="mx-auto mt-12 max-w-2xl bg-paper px-6 py-10 shadow-[0_1px_2px_rgb(60_40_20_/_0.15),0_20px_40px_-30px_rgb(60_40_20_/_0.6)] sm:px-12 sm:py-14 md:mt-16">
        {isLoading || !data ? (
          <div className="space-y-4">
            <Skeleton className="h-14 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        ) : (
          <>
            <h1 className="font-display text-center text-[2.6rem] leading-[1.02] font-semibold text-balance md:text-[3.6rem]">{data.title}</h1>
            <Ornament className="mt-6 mb-12" />
            <Markdown source={data.body} />
          </>
        )}
      </div>
    </article>
  );
}
